import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { UltureAppMark } from "@/components/branding/UltureAppMark";
import { cn } from "@/lib/utils";

interface BrandedErrorScreenProps {
  /** Optional override for the title key (defaults to brandedError.title). */
  titleKey?: string;
  /** Optional override for the subtitle key (defaults to brandedError.subtitle). */
  subtitleKey?: string;
  /** Called when the user taps the primary retry button. Defaults to window.location.reload(). */
  onRetry?: () => void;
  /** Called when the user taps the secondary "back home" link. If omitted, link is hidden. */
  onHome?: () => void;
  className?: string;
}

/**
 * Calm, Ulture-branded screen shown when something went wrong.
 * No raw error codes, no Lovable references, no English fallbacks inside t().
 * Used by ErrorBoundary (uncaught crashes) and AuthErrorPage (OAuth failures).
 */
export const BrandedErrorScreen = memo(function BrandedErrorScreen({
  titleKey = "brandedError.title",
  subtitleKey = "brandedError.subtitle",
  onRetry,
  onHome,
  className,
}: BrandedErrorScreenProps) {
  const { t } = useTranslation();

  const handleRetry = () => {
    if (onRetry) onRetry();
    else window.location.reload();
  };

  return (
    <div
      className={cn(
        "min-h-[100dvh] w-full flex items-center justify-center p-6",
        "bg-gradient-to-br from-[#eaf4fb] via-white to-[#f4faff]",
        className
      )}
      style={{
        paddingTop: "max(var(--safe-area-top, env(safe-area-inset-top, 0px)), 24px)",
        paddingBottom: "max(var(--safe-bottom), 24px)",
      }}

    >
      <div className="max-w-md w-full text-center space-y-7">
        <div className="flex justify-center">
          <UltureAppMark size="lg" tone="dark" iconOnly />
        </div>

        <div className="space-y-3">
          <h1 className="text-2xl font-bold text-foreground tracking-tight">
            {t(titleKey)}
          </h1>
          <p className="text-foreground/70 text-base leading-relaxed">
            {t(subtitleKey)}
          </p>
        </div>

        <div className="flex flex-col items-center gap-3 pt-2">
          <Button
            onClick={handleRetry}
            className="h-12 px-8 rounded-xl bg-[#2E84DE] hover:opacity-95 text-white font-semibold text-base shadow-lg shadow-[#2E84DE]/25"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t("brandedError.retry")}
          </Button>
          {onHome && (
            <button
              type="button"
              onClick={onHome}
              className="text-sm font-medium text-foreground/60 hover:text-foreground transition-colors underline-offset-4 hover:underline"
            >
              {t("brandedError.home")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default BrandedErrorScreen;
